import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import {
  Chain,
  MainWalletPurpose,
  MainWalletTransactionType,
  useGetMainWalletTransactionsQuery,
} from 'src/generated/graphql';
import { formatDate } from 'src/utils/formatDate';
import { getScannerUrl } from 'src/utils/getScannerUrl';

export type MainWalletTransactionHistoryDialogProps = {
  organizationId: string;
  open: boolean;
  onClose: () => void;
  title: string;
  filterType: MainWalletTransactionType;
};

const fmtUSD = (v: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    parseFloat(v) || 0
  );

const statusChip = (status: string) => {
  let color: 'success' | 'error' | 'warning' | 'default' = 'default';
  if (status === 'SUCCESS') color = 'success';
  if (status === 'FAILED') color = 'error';
  if (status === 'REJECTED') color = 'error';
  if (status === 'PENDING') color = 'warning';
  return (
    <Chip label={status} size="small" color={color} sx={{ height: 22, fontSize: '0.7rem' }} />
  );
};

export function MainWalletTransactionHistoryDialog({
  organizationId,
  open,
  onClose,
  title,
  filterType,
}: MainWalletTransactionHistoryDialogProps) {
  const [page, setPage] = useState(1);
  const [hideSmall, setHideSmall] = useState(true);
  const pageSize = 10;

  const [{ data, fetching }] = useGetMainWalletTransactionsQuery({
    variables: {
      organizationId,
      purpose: MainWalletPurpose.Default,
      page,
      limit: pageSize,
      hideSmall,
      smallUsdThreshold: 1,
      filters: { types: [filterType] },
    },
    pause: !open,
  });

  useEffect(() => {
    if (open) {
      setPage(1);
    }
  }, [open, filterType, organizationId]);

  const rows = data?.getMainWalletTransactions.transactions ?? [];
  const total = data?.getMainWalletTransactions.totalCount ?? 0;
  const hasMore = page * pageSize < total;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={hideSmall}
                onChange={(event) => {
                  setHideSmall(event.target.checked);
                  setPage(1);
                }}
              />
            }
            label="Hide phishing (&lt; $1.00)"
          />
        </Box>
        {fetching && !rows.length ? (
          <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 1 }} />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Token</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">USD</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Tx</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((tx) => (
                  <TableRow key={tx.id}>
                    <TableCell>
                      {formatDate(tx.confirmedAt || tx.createdAt, 'condensed')}
                    </TableCell>
                    <TableCell>{tx.mint.symbol}</TableCell>
                    <TableCell align="right">{tx.uiAmount}</TableCell>
                    <TableCell align="right">{fmtUSD(tx.usdAmount)}</TableCell>
                    <TableCell>{statusChip(tx.status)}</TableCell>
                    <TableCell>
                      {getScannerUrl('transaction', tx.txHash, tx.mint.chain as Chain)}
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && !fetching && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" variant="body2">
                        No transactions yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {total} total
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              size="small"
              disabled={!hasMore}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
